import React, { useContext, useEffect, useState } from "react";
import { Grid2, Card, Button, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import dayjs from "dayjs";
import MenuBar from "../common/MenuBar";
import { calculateAge, formatAge, showLoadingImage } from "../Utility/Utility";
import { DataGrid } from "@mui/x-data-grid";
import { getReportData } from "../API/ReportAPI";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import GenericActionButton from "../common/GenericActionButton";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { exportPDF } from "../Utility/PdfConverterForReport";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";

const reportTypeOptions = {
  "Patients ": ["New Patients "],
  // "Financial ": ["Revenue "],
  // "Clinical ": ["Lab Test", "Treatment", "Care Plan "],
  // "Staff ": ["Staff Attendance", "Doctor Performance "],
  // "Lead ": ["New Lead ", "Lead Conversion "],
};

function Report() {
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  const localStorageKey = "ReportFormData";
  const savedData = JSON.parse(localStorage.getItem(localStorageKey)) || {};

  const [reportCategory, setReportCategory] = useState(
    savedData.reportCategory || ""
  );
  const [reportType, setReportType] = useState(savedData.reportType || "");
  const [startDate, setStartDate] = useState(
    savedData.startDate ? dayjs(savedData.startDate) : null
  );
  const [endDate, setEndDate] = useState(
    savedData.endDate ? dayjs(savedData.endDate) : null
  );

  useEffect(() => {
    localStorage.setItem(
      localStorageKey,
      JSON.stringify({
        reportCategory,
        reportType,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      })
    );
  }, [reportCategory, reportType, startDate, endDate]);

  let facilityLogo = auth.selectedFacility.logo;

  const hiddenColumns = [
    "id",
    "dateOfBirth",
    "emailId",
    "isEmergencyContactAvailable",
    "addressCountry",
    "emergencyContactRelationship",
  ];

  const handleShowReport = async () => {
    // Logic to fetch and display report based on selected criteria
    setShowSpinner(true);
    // Simulate data fetching
    var result = await getReportData(auth, {
      facilityId: auth.selectedFacility.id,
      reportName: "NewPatients",
      fromDateTimeInUTC: startDate,
      toDateTimeInUTC: endDate,
    });
    if (result.isSuccess) {
      if (result?.data?.data?.length === 0) {
        showSnackbar("No records found for the selected criteria.", "info");
      } else {
        var columns = Object.keys(result?.data?.data[0] || {})
          .filter((key) => !hiddenColumns.includes(key))
          .map((key) => ({
            field: key,
            headerName: getColumnName(key),
            width: 150,
          }));
        setColumns(columns);
        let tempdata = result?.data?.data.map((row) => ({
          ...row,
          ageInYears: formatAge(row.ageInYears),
          createdOn: convertUTCToTimeZoneDateTimeString(
            row.createdOn,
            auth?.selectedFacility?.timeZone
          ),
          lastModifiedOn: convertUTCToTimeZoneDateTimeString(
            row.lastModifiedOn,
            auth?.selectedFacility?.timeZone
          ),
        }));
        setData(tempdata);
      }
    }
    setShowSpinner(false);
  };

  const getColumnName = (key) => {
    switch (key) {
      case "fullName":
        return "Full Name";
      case "firstName":
        return "First Name";
      case "lastName":
        return "Last Name";
      case "mrn":
        return "MRN";
      case "dateOfBirth":
        return "Date of Birth";
      case "gender":
        return "Gender";
      case "emailId":
        return "Email";
      case "mobileNo":
        return "Phone No.";
      case "ageInYears":
        return "Age (Years)";
      case "bloodGroup":
        return "Blood Group";
      case "totalVisits":
        return "Total Visit";
      case "addressCity":
        return "City";
      case "addressState":
        return "State";
      case "addressCountry":
        return "Country";
      case "addressPIN":
        return "PIN Code";
      case "createdOn":
        return "Created On";
      case "lastModifiedOn":
        return "Last Modified On";
      case "maritalStatus":
        return "Marital Status";
      case "isEmergencyContactAvailable":
        return "Emergency Contact Available";
      case "emergencyContactRelationship":
        return "Emergency Contact Relationship";
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  };

  return (
    <Grid2 container spacing={1}>
      {showSpinner && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}

      {/* Form Card - full width */}
      <Grid2 size={12}>
        <Card
          className="outerCard"
          variant="outlined"
          sx={{ margin: "5px", padding: 1 }}
        >
          <Grid2 container spacing={2}>
            {/* Report Category */}
            <Grid2 size={2}>
              <GenericDropDown
                label="Report Category"
                value={reportCategory}
                onChange={(e) => {
                  setReportCategory(e.target.value);
                  setReportType(""); // reset report type
                }}
                options={Object.keys(reportTypeOptions).map((option) => ({
                  value: option,
                  label: option,
                }))}
                fullWidth
              />
            </Grid2>

            {/* Report Type */}
            <Grid2 size={2}>
              <GenericDropDown
                label="Report Type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                options={
                  reportCategory
                    ? reportTypeOptions[reportCategory].map((option) => ({
                        value: option,
                        label: option,
                      }))
                    : []
                }
                fullWidth
                disabled={!reportCategory}
              />
            </Grid2>

            {/* Start Date */}
            <Grid2 size={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <GenericDatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  maxDate={endDate}
                  nofutureDates={true}
                   returnDayjsObject={true} 
                />
              </LocalizationProvider>
            </Grid2>

            {/* End Date */}
            <Grid2 size={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <GenericDatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  minDate={startDate}
                  nofutureDates={true}
                   returnDayjsObject={true} 
                />
              </LocalizationProvider>
            </Grid2>

            <Grid2 size={1} sx={{ display: "flex", alignItems: "center" }}>
              <GenericActionButton
                label="Show"
                variant="contained"
                color="primary"
                onClick={handleShowReport}
                disabled={
                  !reportCategory || !reportType || !startDate || !endDate
                }
                tooltipText="Show"
              />
              {/* <Button
                variant="contained"
                color="primary"
                onClick={handleShowReport}
                disabled={
                  !reportCategory || !reportType || !startDate || !endDate
                }
                fullWidth
              >
                Show
              </Button> */}
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>
      <Grid2 size={12}>
        {/* Report Display Area - can be a table or chart based on report type */}
        {/* For now, just a placeholder */}
        <Card
          className="outerCard"
          variant="outlined"
          sx={{
            minHeight: 300,
            maxHeight: 500,
            overflowX: "auto",
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "5px",
            flexDirection: "column",
          }}
        >
          <Grid2
            size={12}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: "start",
              padding: "8px 8px",
            }}
          >
            <GenericActionButton
              label="Download"
              icon={<ArrowDownwardOutlinedIcon />}
              onClick={() =>
                exportPDF(
                  data,
                  columns,
                  startDate,
                  endDate,
                  reportType,
                  facilityLogo
                )
              }
              disabled={data.length === 0}
              tooltipText="Download"
            />
          </Grid2>
          <DataGrid
            localeText={{
              noRowsLabel: "There are no records to display.",
            }}
            // loading={showSpinner}
            // slotProps={{
            //   loadingOverlay: {
            //     variant: "skeleton",
            //     noRowsVariant: "skeleton",
            //   },
            // }}
            density="compact"
            rows={data}
            columns={columns}
            pageSize={20}
            rowsPerPageOptions={[5 , 10, 15, 20]}
            disableSelectionOnClick
            disableColumnMenu
            sx={{
              width: "100%",
              "& .sticky-column": {
                position: "sticky",
                right: 0, // Keep it on the right side
                backgroundColor: "white", // To make sure it has a solid background
                zIndex: 100, // Ensure it stays on top of other columns
              },
              "& .MuiTablePagination-toolbar": {
                minHeight: "unset",
                alignItems: "center",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        </Card>
      </Grid2>
    </Grid2>
  );
}

export default Report;
